/* ============================================================================
   Lockett Labs filter engine

   Filtering for collections too large for Shopify's own storefront filtering.
   Measured on this store, that ceiling sits between 4,129 products (filters
   correctly) and 12,889 (filter parameters accepted and silently ignored).

   THE SPLIT, AND WHY
     The index answers exactly one question: which product handles match this
     selection. It holds handles and small integer facet codes, nothing else.
     About 120KB gzipped for 12,889 products.

     Everything a shopper sees is fetched when the card is drawn: price, stock,
     title, image. 24 products in parallel completes in 175ms, measured on this
     store. So a price on screen was read from Shopify at render time, not
     copied out of an index that started going stale the moment it was built.

     The alternative was measured and rejected. A live whole-collection index
     means 17.4MB per shopper, of which 87% is variant and image arrays we would
     discard, and it would STILL be stale, just expensively so.

   CARDS ARE CLONED, NOT TEMPLATED
     The grid's first server-rendered card is captured as the template and
     cloned per product. Writing card markup in here would duplicate the Liquid
     and drift from it the first time anyone edits the section, taking the
     watchlist bell, the sold out treatment and the pre-order states with it.
     Cloning means this file never has an opinion about how a card looks.

   IT OWNS FILTERING ONLY
     No cart code, no watchlist code. The section already owns both, and both
     are delegated, so a cloned card is wired the moment it lands in the DOM.
   ========================================================================== */

(function () {
  'use strict';

  /* The merchant sets products per page in the section settings. Hardcoding
     24 here meant the grid rendered 24 while the section's own label, which
     is server rendered from that setting, went on saying 48 on this page. */
  var PAGE_SIZE = 24;
  /* Facets that can be selected, counted and matched.

     `game` is not a column in the index files and does not need to be. A shard
     IS one game, so which game a row belongs to is known from the file it was
     read out of, and it is stamped on at merge time below. That keeps the
     shards unchanged, so this filter works against the index already published
     rather than waiting on a rebuild.

     It only does anything where more than one shard is loaded, which is exactly
     where it is wanted: /collections/all, single-cards, under-25, sale and the
     cross-game shelves. On a single-game page every product answers the same,
     so the group has one value and the UI drops it. */
  var FACETS = ['game', 'product_class', 'series', 'set_name', 'language'];

  /* The four the index files actually carry dictionaries for. */
  var SHARD_FACETS = ['product_class', 'series', 'set_name', 'language'];

  /* ---- index ------------------------------------------------------------ */

  var index = null;
  var rows = null;
  var byHandle = {};

  function loadIndex(url) {
    /* sessionStorage rather than localStorage: the index is disposable and a
       shard is large enough that leaving copies of it on a shopper's device
       across visits is rude when refetching costs one cached request. */
    var key = 'llf:' + url;
    try {
      var cached = sessionStorage.getItem(key);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (e) {}

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('index ' + r.status);
        return r.json();
      })
      .then(function (json) {
        try { sessionStorage.setItem(key, JSON.stringify(json)); } catch (e) {}
        return json;
      });
  }

  /* ---- matching --------------------------------------------------------- *
     A row is [handle, product_class, series, set_name, language, flags], each
     facet an integer index into index.dict, or -1 for absent.

     Selections are OR within a facet and AND across facets, which is how
     Shopify's own filtering behaves and therefore what shoppers expect.      */

  /* 8 is past the end of a published row. It is written client side during the
     merge, so it costs nothing in the index and an older row simply has it
     filled in rather than missing. */
  /* 8 is the best seller rank, written into the shard by refresh-stock.js from
     the store's own BEST SELLING PRODUCTS collection order. 9 is `game`, which
     is not in the file at all: it is stamped on at merge time from the shard a
     row came out of. Keep them in that order, because moving `game` onto 8
     would overwrite the rank. */
  var COL = { product_class: 1, series: 2, set_name: 3, language: 4, flags: 5, price: 6, created: 7, bestseller: 8, game: 9, order: 10 };
  var F_PREORDER = 1;
  var F_IN_STOCK = 2;
  var F_ON_SALE = 4;

  /* SECONDS since epoch, matching the index column. Day resolution made every
     upload on a given day tie, and ties fell back to index order, so newest
     first missed the actual latest product. Recomputed per call. */
  function newerThan(days) {
    return Math.floor(Date.now() / 1000) - (days * 86400);
  }

  function inStock(row) { return (row[COL.flags] & F_IN_STOCK) === F_IN_STOCK; }
  function isPreorder(row) { return (row[COL.flags] & F_PREORDER) === F_PREORDER; }

  function matches(row, sel, skipFacet) {
    for (var i = 0; i < FACETS.length; i++) {
      var facet = FACETS[i];
      if (facet === skipFacet) continue;
      var chosen = sel[facet];
      if (!chosen || !chosen.length) continue;
      if (chosen.indexOf(row[COL[facet]]) === -1) return false;
    }
    if (sel.preorder && !isPreorder(row)) return false;
    if (sel.onSale && (row[COL.flags] & F_ON_SALE) !== F_ON_SALE) return false;
    if (sel.newIn && row[COL.created] < newerThan(30)) return false;

    /* Price is a range rather than a set of values, so it is checked here
       rather than through the dictionary path. Bounds are in pence, matching
       the column, so nothing has to round mid comparison. */
    if (skipFacet !== 'price') {
      if (sel.priceMin != null && row[COL.price] < sel.priceMin) return false;
      if (sel.priceMax != null && row[COL.price] > sel.priceMax) return false;
    }

    /* Availability is three states, not two. A pre-order is not sold out, it
       has not arrived: it was being counted as Out of stock, so a shopper
       filtering for what they could not buy today was shown a wall of Delta
       Reign blisters they could buy today, and there was no way to ask for the
       pre-orders on their own. The card grid has always drawn them as their own
       "Coming Soon" state; this makes the filter agree with the card.

       Selecting every state means the same as selecting none, so it must not
       filter everything out. */
    if (skipFacet !== 'availability' && sel.availability &&
        sel.availability.length && sel.availability.length < 3) {
      if (sel.availability.indexOf(availabilityOf(row)) === -1) return false;
    }
    return true;
  }

  /* One row, one state. Pre-order wins over stock, because a pre-order that
     Shopify reports as buyable is still a pre-order. */
  function availabilityOf(row) {
    if (isPreorder(row)) return 'coming';
    return inStock(row) ? 'in' : 'out';
  }

  /* Sold out sinks, always, unless the shopper asked for sold out specifically.

     A card shop is mostly single copies, so an unsorted grid puts a wall of
     things nobody can buy in front of the things they can. Ordering is stable
     within each block: equal rows keep their index order, so the grid does not
     reshuffle between renders. */
  function sortRows(list, sortKey, sel) {
    /* The sink only makes sense while the shopper is looking at things they can
       buy. Asking for sold out, or for pre-orders, means those ARE the results,
       so nothing should sink. */
    var onlyOutOfStock = sel.availability && sel.availability.length === 1 &&
                         (sel.availability[0] === 'out' || sel.availability[0] === 'coming');

    return list.sort(function (a, b) {
      if (!onlyOutOfStock) {
        /* A pre-order is buyable, so it does not sink. The state already says
           this everywhere else on the card, but the sink was binary on stock
           alone, which lumped pre-orders in with sold out. On search that was
           the whole answer: "delta reign" put a 39p Delibird from CHILLING
           REIGN first, because it was in stock, above all eleven Delta Reign
           pre-orders Shopify had ranked 1 to 11. Only what nobody can buy
           sinks; everything else keeps the order the sort key asked for, which
           on a search page is Shopify's own relevance. */
        var av = (inStock(a) || isPreorder(a)) ? 0 : 1;
        var bv = (inStock(b) || isPreorder(b)) ? 0 : 1;
        if (av !== bv) return av - bv;
      }
      if (sortKey === 'price-asc') return a[COL.price] - b[COL.price];
      if (sortKey === 'price-desc') return b[COL.price] - a[COL.price];
      if (sortKey === 'date-asc') return a[COL.created] - b[COL.created];
      if (sortKey === 'date-desc') return b[COL.created] - a[COL.created];

      /* Best selling. Rank 0 is the store's best seller, so it sorts ASCENDING.
         -1 means the product was not in the ranking at all, which is a draft or
         something the last sweep could not reach, and it has to sort LAST
         rather than first. Ties and unranked products fall back to the
         collection's own order, which `featured` also uses. */
      if (sortKey === 'best-selling') {
        var ar = a[COL.bestseller], br = b[COL.bestseller];
        if (typeof ar !== 'number' || ar < 0) ar = Infinity;
        if (typeof br !== 'number' || br < 0) br = Infinity;
        if (ar !== br) return ar - br;
        return (a[COL.order] || 0) - (b[COL.order] || 0);
      }

      /* The collection's own order, which is what Shopify would have shown.
         See the ORDER column note in init(). */
      if (sortKey === 'featured') return (a[COL.order] || 0) - (b[COL.order] || 0);

      /* Alphabetical sorts on the HANDLE, not the title, because the index
         deliberately carries no titles: adding them would roughly double the
         shard for one sort. A handle is generated from its title, so the order
         is the same apart from case and punctuation, which is close enough for
         browsing and free. */
      if (sortKey === 'title-asc') return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0;
      if (sortKey === 'title-desc') return a[0] > b[0] ? -1 : a[0] < b[0] ? 1 : 0;
      return 0;
    });
  }

  function apply(sel, sortKey) {
    var hits = [];
    for (var i = 0; i < rows.length; i++) {
      if (matches(rows[i], sel)) hits.push(rows[i]);
    }
    sortRows(hits, sortKey, sel);
    return hits.map(function (r) { return r[0]; });
  }

  /* Counts for a facet are computed with that facet's own selection removed.
     Otherwise picking "Sealed" drops every other product_class count to zero
     and the shopper cannot see what else is available without undoing their
     choice first. */
  function counts(sel) {
    var result = {};
    FACETS.forEach(function (facet) {
      var col = COL[facet];
      var tally = {};
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        if (!matches(row, sel, facet)) continue;
        var code = row[col];
        if (code === -1) continue;
        tally[code] = (tally[code] || 0) + 1;
      }
      result[facet] = tally;
    });

    var pre = 0;
    for (var j = 0; j < rows.length; j++) {
      if (isPreorder(rows[j]) && matches(rows[j], sel, '__preorder__')) pre++;
    }
    result.preorder = pre;

    var sale = 0;
    var fresh = 0;
    var cutoff = newerThan(30);
    for (var q = 0; q < rows.length; q++) {
      if (!matches(rows[q], sel, '__flags__')) continue;
      if ((rows[q][COL.flags] & F_ON_SALE) === F_ON_SALE) sale++;
      if (rows[q][COL.created] >= cutoff) fresh++;
    }
    result.onSale = sale;
    result.newIn = fresh;

    /* Counted with availability's own selection removed, for the same reason
       the facet counts are: picking "In stock" must not drop the out of stock
       count to zero and hide how much else there is. */
    var stock = { in: 0, coming: 0, out: 0 };
    for (var k = 0; k < rows.length; k++) {
      if (!matches(rows[k], sel, 'availability')) continue;
      stock[availabilityOf(rows[k])]++;
    }
    result.availability = stock;

    /* Two things the price UI needs, both computed with the price selection
       itself removed: the bracket counts, and the true range of the rest of
       the selection. Without the second the slider bounds would collapse to
       whatever the shopper last chose and they could never widen it again. */
    var brackets = { 2500: 0, 7500: 0, 10000: 0 };
    var lo = null;
    var hi = null;
    for (var m = 0; m < rows.length; m++) {
      if (!matches(rows[m], sel, 'price')) continue;
      var value = rows[m][COL.price];
      if (value <= 2500) brackets[2500]++;
      if (value <= 7500) brackets[7500]++;
      if (value <= 10000) brackets[10000]++;
      /* Zero is a pre-order with no price yet, not a 0p product, so it must
         not drag the bottom of the range down to nothing. */
      if (value > 0) {
        if (lo === null || value < lo) lo = value;
        if (hi === null || value > hi) hi = value;
      }
    }
    result.priceBrackets = brackets;
    result.priceRange = { min: lo === null ? 0 : lo, max: hi === null ? 0 : hi };

    return result;
  }

  /* ---- live product data ------------------------------------------------ *
     Only ever for the page being shown. Failures are dropped rather than
     retried: a product deleted since the index was built should quietly not
     appear, not stall the grid or render an empty card.                      */

  function hydrate(handles) {
    return Promise.all(handles.map(function (h) {
      return fetch('/products/' + h + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (list) {
      return list.filter(Boolean);
    });
  }

  /* ---- rendering -------------------------------------------------------- */

  /* ---- the call-to-action contract -----------------------------------------
     The collection card and the search card carry the same states but not the
     same markup: one has a .llc__btn carrying data-atc, the other a .lls__cta
     carrying data-lls-add with the variant id in its own attribute, and each
     page has its own handler listening for its own attribute. Writing the
     collection's names onto a search card produced a button that looked right
     and did nothing.

     So the names are configuration, defaulted to the collection's, and the
     search page passes its own. The WATCH contract needed no such treatment:
     both pages already use data-watch-cta with data-handle. */
  var CTA = { sel: '.llc__btn', atc: 'data-atc', atcId: null, classes: true, mini: null };

  var template = null;
  var templateTitle = '';

  function captureTemplate(grid) {
    if (template) return template;
    var first = grid.querySelector('.llc__card');
    if (!first) return null;
    template = first.cloneNode(true);
    /* Every aria-label on the template names whichever product the server
       rendered first ("Add to cart: X", "View X", "Watch X"), so the clone loop
       has to swap that name back out. The image alt is the one place the raw
       untrimmed title is still readable: the visible title may already have
       been split into a name, a card number and a set chip. */
    var tplImg = template.querySelector('.llc__media img');
    templateTitle = (tplImg && tplImg.getAttribute('alt')) || '';
    return template;
  }

  /* ---- the game chip ----------------------------------------------------- *
     The chip naming the game is the one field on the card that is neither a
     price nor a string off the product: the section resolves it in Liquid from
     a table of "match|label|accent|ink|icon" rows, first match wins. A clone
     that does not rewrite it keeps the TEMPLATE card's game, so every card in
     a filtered grid claimed whichever game the server happened to render
     first. On /collections/one-piece-tcg that put a One Piece chip and a
     pirate-skull icon on Pokemon cards.

     The table is passed in from the section rather than restated here, so
     there is still exactly one place a brand is defined. */

  var brands = [];
  var gameLabel = '';

  function setBrands(raw) {
    brands = String(raw || '').split(';;').map(function (row) {
      var cells = row.split('|');
      if (cells.length < 5) return null;
      return {
        needles: cells[0].split(',').map(function (n) { return n.trim().toLowerCase(); }),
        label: cells[1],
        acc: cells[2],
        ink: cells[3],
        /* The table is authored for HTML, so its icon URLs carry &amp;. Setting
           that on src verbatim requests a literally different file. */
        icon: cells[4].replace(/&amp;/g, '&')
      };
    }).filter(Boolean);
  }

  function resolveBrand(product) {
    var hay = (
      (product.vendor || '') + ' ' +
      (product.type || product.product_type || '') + ' ' +
      (product.tags || []).join(',')
    ).toLowerCase();

    for (var i = 0; i < brands.length; i++) {
      for (var n = 0; n < brands[i].needles.length; n++) {
        if (brands[i].needles[n] && hay.indexOf(brands[i].needles[n]) > -1) return brands[i];
      }
    }
    /* Nothing matched, so the chip says the plain vendor with no mark, which
       is what the Liquid does. It must still be rewritten: leaving it alone is
       how the wrong game got shown in the first place. */
    return { label: product.vendor || gameLabel || '', acc: '', ink: '', icon: '' };
  }

  function fillBrand(node, product) {
    var chip = node.querySelector('.llc__vendor');
    if (!chip) return;

    var brand = resolveBrand(product);
    var img = chip.querySelector('img');

    if (brand.icon) {
      if (!img) {
        img = document.createElement('img');
        img.setAttribute('width', '12');
        img.setAttribute('height', '12');
        img.setAttribute('alt', '');
        img.setAttribute('decoding', 'async');
        chip.insertBefore(img, chip.firstChild);
      }
      img.setAttribute('src', brand.icon);
      img.removeAttribute('loading');
    } else if (img) {
      img.remove();
      img = null;
    }

    /* Only the label text is replaced, so the icon node survives. */
    var text = brand.label || '';
    for (var c = chip.childNodes.length - 1; c >= 0; c--) {
      if (chip.childNodes[c].nodeType === 3) chip.removeChild(chip.childNodes[c]);
    }
    chip.appendChild(document.createTextNode(text));

    if (brand.acc) {
      chip.style.setProperty('--v-acc', brand.acc);
      chip.style.setProperty('--v-ink', brand.ink);
    } else {
      chip.style.removeProperty('--v-acc');
      chip.style.removeProperty('--v-ink');
    }
  }

  /* Hiding a card element is not just the hidden property. Every one of these
     is display:inline-flex or display:flex in the section's own stylesheet,
     which outranks the browser's [hidden] rule, so a "hidden" element stayed
     on screen still showing the template card's value. A Pokemon figurine was
     displaying the card number OP16-050 this way. The stylesheet now carries
     the [hidden] guard; the text is cleared here too, so a stale value cannot
     survive even if that rule is ever lost. */
  function hideField(el) {
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  /* Shopify handleizes an apostrophe to a hyphen, so the set metaobject
     "The World's Strongest Warriors" is held in the index as
     the-world-s-strongest-warriors. De-handleizing that naively printed
     "The World S Strongest Warriors" on every card the filter drew. A lone s
     between hyphens is a possessive in this vocabulary, never a word.

     Capitalising on \b is the other half of the same bug: an apostrophe is a
     word boundary, so \b\w also hits the s in "World's". Case is applied per
     space-delimited word instead. */
  function fromHandle(handle) {
    return String(handle)
      .replace(/-s-/g, "'s-")
      .replace(/-s$/, "'s")
      .replace(/-/g, ' ')
      .replace(/(^|\s)\S/g, function (m) { return m.toUpperCase(); });
  }

  /* Titles shout the set: "Fossa : THE WORLD'S STRONGEST WARRIORS - OP17-010".
     Cased down to match the sets that come from the index, so one grid cannot
     show the same set two ways. Words holding a digit (OP17, SV1) keep their
     case, and a label that is already mixed case is left alone. */
  function tidySet(text) {
    var s = String(text || '').trim();
    if (!s || s !== s.toUpperCase()) return s;
    return s.replace(/\S+/g, function (w) {
      return /\d/.test(w) ? w : w.charAt(0) + w.slice(1).toLowerCase();
    });
  }

  function money(cents, format) {
    var value = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{') > -1) {
      return format.replace(/\{\{\s*amount\s*\}\}/, value);
    }
    return '£' + value;
  }

  function fillCard(node, product) {
    var variant = (product.variants && product.variants[0]) || {};
    var available = !!product.available;

    /* Read from the LIVE product rather than the index, so a product tagged
       pre-order five minutes ago is treated as one now. */
    var tags = (product.tags || []).map(function (t) { return String(t).toLowerCase(); });
    var preorder = tags.indexOf('pre-order') > -1 || tags.indexOf('preorder') > -1;

    var price = variant.price != null ? variant.price : product.price;
    var pricePending = !price;

    /* A pre-order is not sold out, it has not arrived. Marking it soldout gave
       it the desaturated treatment, which reads as "you missed this" on the
       one product type where the opposite is true. It gets its own state, and
       the fade only ever keys off soldout. */
    var state = preorder ? 'coming' : (available ? 'live' : 'soldout');
    node.setAttribute('data-state', state);
    node.setAttribute('data-handle', product.handle);

    /* Anything INSIDE the card that carries its own handle is a second copy of
       the card's identity, and the clone keeps the template's copy unless it is
       rewritten here. The watchlist bell is exactly that: every cloned card had
       the first-rendered product's handle on its bell, so clicking one bell
       wrote that handle and syncBells() then lit every bell in the grid, all
       pointing at a product the shopper had not touched. Reset the pressed
       state with it, or a clone taken from a watched card shows as watched. */
    var owners = node.querySelectorAll('[data-handle]');
    for (var o = 0; o < owners.length; o++) {
      owners[o].setAttribute('data-handle', product.handle);
      if (owners[o].hasAttribute('aria-pressed')) owners[o].setAttribute('aria-pressed', 'false');
      owners[o].classList.remove('is-watching', 'is-watch-ok');
    }

    /* The template is cloned from whichever card the server rendered first, so
       every state-dependent bit has to be rewritten or it carries that card's
       state. This badge was the clearest case: sold out products were being
       shown reading "In Stock". */
    var badge = node.querySelector('.llc__badge:not(.llc__badge--meta):not(.llc__badge--sale)');
    if (badge) {
      var badgeState = preorder ? 'coming' : (available ? 'instock' : 'soldout');
      badge.setAttribute('data-s', badgeState);
      badge.textContent = preorder ? 'Coming Soon' : (available ? 'In Stock' : 'Sold Out');
    }

    var link = node.querySelectorAll('a[href]');
    for (var i = 0; i < link.length; i++) {
      if ((link[i].getAttribute('href') || '').indexOf('/products/') > -1) {
        link[i].setAttribute('href', '/products/' + product.handle);
      }
    }

    var img = node.querySelector('.llc__media img');
    if (img && product.featured_image) {
      img.setAttribute('src', product.featured_image);
      img.setAttribute('srcset', '');
      img.setAttribute('alt', product.title || '');
      /* The template's img ships loading=lazy and this used to strip it, so a
         re-render turned all 106 grid images eager: 3.4 MB fetched at once on
         a phone, every one of them below the fold. Setting it back rather
         than removing it keeps in-viewport images loading immediately, which
         is what lazy already does, and defers the rest. */
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
    }

    /* data-url is another second copy of the card's identity, like the handles
       above, and nothing was rewriting it: the card for
       charizard-evolutions-holo-11-108 still carried the template product's
       URL. Neither of these two templates reads it today, so nothing is broken
       on screen, but the hub sections navigate with
       card.getAttribute('data-url'). Left as it was, it is a trap for whoever
       wires that pattern up here next. */
    if (node.hasAttribute('data-url')) {
      node.setAttribute('data-url', '/products/' + product.handle);
    }

    /* Labels are the one field a sighted shopper never sees, which is why this
       survived: every cloned card announced the first-rendered product, so a
       screen reader heard the same 24 links on a search results page. The
       prefixes vary ("Add to cart: ", "View ", "Watch "), so the product name
       is swapped inside the label that is already there rather than the label
       being rebuilt from a list of patterns this function would have to keep
       in step with two different card templates. */
    if (templateTitle) {
      var labelled = node.querySelectorAll('[aria-label]');
      for (var a = 0; a < labelled.length; a++) {
        var lab = labelled[a].getAttribute('aria-label') || '';
        if (lab.indexOf(templateTitle) > -1) {
          labelled[a].setAttribute('aria-label', lab.split(templateTitle).join(product.title || ''));
        }
      }
    }

    /* The section splits a title like "Whismur : FATES COLLIDE - 80/124" into a
       trimmed name, a card-number badge and a set chip. The clone has to split
       it the same way, or the grid visibly rewrites every title back to the raw
       one the moment filtering takes over. Mirrors the Liquid: everything
       before the last " - " is the head, and the name is the head up to " : ".

       A card number always contains a digit. Without that test the tail of
       "Sealed Booster Box - English" passes as one, which is why the section
       excludes sealed and accessories from this rule entirely. */
    var rawTitle = product.title || '';
    var titleParts = rawTitle.split(' - ');
    var tail = titleParts[titleParts.length - 1].trim();
    var looksLikeNumber =
      titleParts.length > 1 &&
      /\d/.test(tail) &&
      /^[A-Z0-9]+[/-]?[A-Z0-9]*$/i.test(tail) &&
      tail.length <= 12;

    /* Only trim when the card has somewhere to put the parts. The collection
       card renders the number into .llc__badge--meta and the set into
       .llc__trait, so taking them out of the title loses nothing. The search
       card renders neither, and the same trim silently threw both away: a
       search for charizard returned eleven results all reading "Charizard" at
       eleven different prices, with no set, number or rarity to tell a £2.45
       card from an £89.95 one. setFromTitle is still derived either way, so a
       card that gains a chip later needs no further change here. */
    var canSplitTitle = !!node.querySelector('.llc__badge--meta');

    var displayTitle = rawTitle;
    var setFromTitle = '';
    if (looksLikeNumber) {
      var head = titleParts.slice(0, -1).join(' - ').split(' : ');
      var name = head[0].trim();
      if (name && canSplitTitle) displayTitle = name;
      /* The section reads the set out of the same title when a product has no
         set metafield, and drops any parenthetical: "PROMO (Holo)" is the
         promo set, not a set of its own. */
      if (head.length > 1) {
        setFromTitle = head[head.length - 1]
          .replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim();
      }
    }

    var title = node.querySelector('.llc__card-title');
    if (title) title.textContent = displayTitle;

    /* A zero price on a pre-order means the price is not set yet, not that the
       product is free. Showing £0.00 invites a support message at best and a
       complaint at worst. */
    var priceEl = node.querySelector('.llc__price');
    if (priceEl) {
      priceEl.setAttribute('data-pending', pricePending ? '1' : '0');
      if (pricePending) {
        priceEl.textContent = 'Price Pending';
      } else {
        var wasPrice = (variant.compare_at_price || 0) > price
          ? ' <s>' + money(variant.compare_at_price) + '</s>' : '';
        priceEl.innerHTML = money(price) + wasPrice;
      }
    }

    /* The watchlist bell keys off the handle, so it has to be re-pointed or a
       cloned card would toggle whichever product it was cloned from. */
    var watch = node.querySelector('[data-watch]');
    if (watch) {
      watch.setAttribute('data-watch', product.handle);
      watch.setAttribute('aria-pressed', 'false');
    }

    /* EVERY field that varies per product has to be rewritten, or the clone
       keeps the template card's value. Two were being missed and both showed
       plainly wrong data on every card in the grid: the meta badge kept the
       template's card number (four different cards all reading 015/088) and
       the trait chip kept its set (all reading Perfect Order). Wrong card
       numbers on a singles catalogue are worse than no card numbers. */
    var meta = node.querySelector('.llc__badge--meta');
    if (meta) {
      /* Titles here end in the card number: "Scorbunny : PROMO (Holo) -
         SWSH071", "Whismur : FATES COLLIDE - 80/124". Nothing else in the
         live product carries it, so it is read back off the title, and the
         badge is removed rather than left lying when there is none. */
      if (looksLikeNumber) {
        meta.textContent = tail;
        meta.hidden = false;
      } else {
        hideField(meta);
      }
    }

    /* The game chip, which the clone would otherwise inherit wholesale. */
    fillBrand(node, product);

    var trait = node.querySelector('.llc__trait');
    if (trait) {
      var row = byHandle[product.handle];
      var setCode = row ? row[COL.set_name] : -1;
      var setName = (setCode > -1 && index.dict.set_name) ? index.dict.set_name[setCode] : null;
      /* The index is not the only place a set is known, and it is the slower
         one: a product uploaded since the last full rebuild carries no set
         facet, so the chip the section had already rendered from the title was
         being wiped the moment filtering took over. Same cascade as the
         section: curated set first, then the title. */
      var setLabel = setName ? fromHandle(setName) : tidySet(setFromTitle);
      if (setLabel) {
        trait.textContent = setLabel;
        trait.hidden = false;
      } else {
        hideField(trait);
      }
      /* An empty chip row still draws its gap, so the wrapper goes with it. */
      var traitWrap = trait.parentNode;
      if (traitWrap && traitWrap.classList && traitWrap.classList.contains('llc__traits')) {
        traitWrap.hidden = !!trait.hidden;
      }
    }

    /* Sale badge. compare_at_price above price is what Shopify means by on
       offer, and the section renders the saving as a percentage, so this
       matches rather than inventing a second way of saying it. */
    var saleBadge = node.querySelector('.llc__badge--sale');
    var compareAt = variant.compare_at_price || product.compare_at_price || 0;
    var onSale = compareAt > 0 && price > 0 && compareAt > price;
    if (onSale) {
      if (!saleBadge) {
        saleBadge = document.createElement('span');
        /* Match whatever THIS page calls a badge. Hardcoding llc__badge built
           a span the search page has no rule for, so the saving rendered as
           bare link-blue text stretched across the artwork, next to a state
           pill that was styled correctly. The state badge is the sample. */
        var badgeBase = badge
          ? badge.className.replace(/\s*llc__badge--\S+/g, '').trim()
          : 'llc__badge';
        saleBadge.className = badgeBase + ' llc__badge--sale';
        saleBadge.setAttribute('data-s', 'sale');
        var badgeHost = node.querySelector('.llc__badges');
        if (badgeHost) badgeHost.appendChild(saleBadge);
      }
      saleBadge.textContent = '-' + Math.round((1 - price / compareAt) * 100) + '%';
      saleBadge.hidden = false;
      node.setAttribute('data-sale', '1');
    } else {
      if (saleBadge) saleBadge.hidden = true;
      node.removeAttribute('data-sale');
    }

    /* A low-stock count ("3 left") is not in the index, so a clone has no way
       to verify one. Left alone it keeps the template card's number and states
       a quantity belonging to a different product, which is worse than saying
       nothing. Hidden rather than removed, so the node survives for whichever
       card is templated next. */
    var lowBadge = node.querySelector('.llc__badge--low');
    if (lowBadge) {
      lowBadge.hidden = true;
      lowBadge.textContent = '';
    }

    /* THE CALL TO ACTION.

       This looked for [data-add]. The cards are built with data-atc, so it
       matched nothing and the clone simply kept whichever button the template
       card happened to have. On a grid templated from an in-stock card, every
       Coming Soon pre-order therefore offered "Add to cart" over the words
       Price Pending.

       Rebuilt from the product's own state, following the section's rule
       rather than inventing a second one: a pre-order is buyable only when it
       actually has stock AND a price. One that has not been released, or has
       no quantity, or has no price yet, cannot be bought at all, so it offers
       the watchlist instead of a control that would fail after the tap. */
    var buyable = available && !pricePending;

    var cta = node.querySelector(CTA.sel);
    if (cta) {
      var full = cta.querySelector('.llc__btn-full');
      var shortL = cta.querySelector('.llc__btn-short');
      var label = function (long, brief) {
        if (full) full.textContent = long; else cta.textContent = long;
        if (shortL) shortL.textContent = brief;
      };

      if (buyable) {
        if (CTA.classes) cta.className = 'llc__btn';
        cta.removeAttribute('data-watch-cta');
        cta.removeAttribute('aria-pressed');
        if (CTA.atcId) { cta.setAttribute(CTA.atc, ''); cta.setAttribute(CTA.atcId, variant.id || ''); }
        else { cta.setAttribute(CTA.atc, variant.id || ''); }
        cta.disabled = false;
        if (preorder) {
          cta.setAttribute('data-preorder', '1');
          label('Pre-order', 'Pre-order');
        } else {
          cta.removeAttribute('data-preorder');
          label('Add to cart', 'Add');
        }
      } else {
        /* Watchlist only. The section already delegates [data-watch-cta], so
           re-pointing the attributes is enough to wire it up. */
        if (CTA.classes) cta.className = 'llc__btn ' + (preorder ? 'llc__btn--soft' : 'llc__btn--sold');
        cta.removeAttribute(CTA.atc);
        if (CTA.atcId) cta.removeAttribute(CTA.atcId);
        cta.removeAttribute('data-preorder');
        cta.disabled = false;
        var addLabel = preorder ? 'Watch for alerts' : 'Watch for restock';
        cta.setAttribute('data-watch-cta', '');
        cta.setAttribute('data-handle', product.handle);
        cta.setAttribute('aria-pressed', 'false');
        cta.setAttribute('data-watch-add', addLabel);
        cta.setAttribute('data-watch-add-short', 'Watch');
        cta.setAttribute('data-watch-remove', 'Remove from Watchlist');
        cta.setAttribute('data-watch-remove-short', 'Unwatch');
        label(addLabel, 'Watch');
      }
    }

    /* THE SECONDARY LINK, on pages that have one.

       It belongs with a call to action that does NOT navigate. Once the button
       is Add to cart, tapping it no longer takes you to the product, so the row
       needs a way through; when the button is a watchlist control it is already
       the only action and a second link under it is noise. Same condition as
       the button itself, so the two cannot disagree.

       TOGGLED, NEVER CREATED. The template card is whichever one the server
       rendered first, and a card cloned from a sold-out template has no View
       link at all: on a search for "Mew" every in-stock card came out without
       one, while "one piece" kept them, purely because of what happened to be
       first. The section renders the link on every card and hides it, so this
       only has to decide. Building it here is how the sale badge ended up as
       unstyled link-blue text: markup invented by the engine does not know
       what a given page calls its classes.

       data-dual travels with it, because it describes what the row contains. */
    if (CTA.mini) {
      var mini = node.querySelector(CTA.mini);
      if (mini) {
        mini.hidden = !buyable;
        var row = mini.parentNode;
        if (row && row.setAttribute) {
          if (buyable) row.setAttribute('data-dual', '1');
          else row.removeAttribute('data-dual');
        }
      }
    }

    return node;
  }

  function render(grid, products, replace) {
    var frag = document.createDocumentFragment();
    products.forEach(function (p) {
      var node = template.cloneNode(true);
      frag.appendChild(fillCard(node, p));
    });
    /* Replacing resets the scroll position implicitly; appending must not, so
       "show more" keeps the shopper where they were. */
    if (replace !== false) grid.innerHTML = '';
    grid.appendChild(frag);

    /* Let the section re-apply anything it owns on these cards, principally
       the watchlist pressed state. */
    document.dispatchEvent(new CustomEvent('ll:filter:rendered', {
      detail: { count: products.length }
    }));
  }

  /* ---- public API ------------------------------------------------------- */

  var api = {
    ready: false,

    /* opts.indexUrls  one or more shards to load and merge
       opts.scope      how to narrow those shards to the collection being
                       viewed: { facet, value, maxPrice, withinDays, members }

       Scoping happens once, here, rather than in every query. Reducing rows
       up front means matching, counting, sorting and paging all operate on
       the collection's products already and none of them had to learn what a
       collection is. */
    init: function (opts) {
      var urls = opts.indexUrls || [opts.indexUrl];
      var scope = opts.scope || null;
      if (opts.pageSize > 0) PAGE_SIZE = opts.pageSize;
      if (opts.brands) setBrands(opts.brands);
      if (opts.cta) {
        if (opts.cta.sel) CTA.sel = opts.cta.sel;
        if (opts.cta.atc) CTA.atc = opts.cta.atc;
        CTA.atcId = opts.cta.atcId || null;
        CTA.classes = opts.cta.classes !== false;
        CTA.mini = opts.cta.mini || null;
      }
      if (opts.gameLabel) gameLabel = opts.gameLabel;

      return Promise.all(urls.map(loadIndex)).then(function (jsons) {
        /* Facet codes are per shard: 'sealed' can be 0 in one and 3 in
           another, so rows cannot simply be concatenated. Each shard's
           dictionary is folded into one merged dictionary and its rows are
           re-coded against it. Collections that span games (Japanese,
           Accessories, Sale) are the whole reason this exists. */
        var merged = {};
        FACETS.forEach(function (f) { merged[f] = []; });

        var all = [];
        var seen = {};

        jsons.forEach(function (json) {
          var remap = {};
          SHARD_FACETS.forEach(function (f) {
            var d = (json.dict && json.dict[f]) || [];
            remap[f] = d.map(function (value) {
              var at = merged[f].indexOf(value);
              if (at === -1) { merged[f].push(value); at = merged[f].length - 1; }
              return at;
            });
          });

          /* The shard names its own game, so every row out of this file gets
             the same code. 'all' is what a single unsharded build writes and
             means "not a game", so it is left unset rather than offered as a
             filter value. */
          var gameCode = -1;
          if (json.game && json.game !== 'all') {
            gameCode = merged.game.indexOf(json.game);
            if (gameCode === -1) {
              merged.game.push(json.game);
              gameCode = merged.game.length - 1;
            }
          }

          var src = json.products || [];
          for (var n = 0; n < src.length; n++) {
            var handle = src[n][0];
            /* A product relocated between shards can briefly exist in both.
               First shard wins, so it is listed once. */
            if (seen[handle]) continue;
            seen[handle] = 1;

            var row = src[n].slice();
            for (var f2 = 0; f2 < SHARD_FACETS.length; f2++) {
              var col = COL[SHARD_FACETS[f2]];
              var was = row[col];
              row[col] = (was >= 0 && remap[SHARD_FACETS[f2]][was] != null)
                ? remap[SHARD_FACETS[f2]][was] : -1;
            }
            row[COL.game] = gameCode;
            /* undefined compares false against everything, which once made
               the bracket counts and the range filter disagree by exactly
               the 279 rows that had no price. Normalise once, here. */
            if (typeof row[6] !== 'number') row[6] = 0;
            if (typeof row[7] !== 'number') row[7] = 0;
            all.push(row);
          }
        });

        if (scope) {
          if (scope.members) {
            /* THE ORDER COLUMN.

               /collections/<handle>/products.json returns products in THAT
               COLLECTION'S own default order, and ignores ?sort_by entirely.
               So the membership list already fetched for this page is not just
               a set, it is the exact order Shopify would have rendered:
               Newest for POKEMON, Best selling for Our Latest Deals, or a hand
               sorted manual order.

               Recording each product's position is therefore free, and it is
               the only way to honour a manual or best selling default, neither
               of which can be derived from anything in the index. Without it
               "Featured" meant shard order, which is the order the builder
               happened to read products in, and it silently overrode whatever
               sort the merchant had set on the collection.

               Position is stored 1-based so a missing value stays falsy. */
            var want = {};
            for (var m = 0; m < scope.members.length; m++) want[scope.members[m]] = m + 1;
            all = all.filter(function (r) { return want[r[0]] > 0; });
            for (var o = 0; o < all.length; o++) all[o][COL.order] = want[all[o][0]];
          }
          if (scope.facet && scope.value) {
            var scol = COL[scope.facet];
            var code = (merged[scope.facet] || []).indexOf(scope.value);
            all = code === -1 ? [] : all.filter(function (r) { return r[scol] === code; });
          }
          if (scope.maxPrice > 0) {
            /* A price of 0 means Price Pending, not free, so it does not
               belong in an Under 25 collection. */
            all = all.filter(function (r) { return r[6] > 0 && r[6] <= scope.maxPrice; });
          }
          if (scope.withinDays > 0) {
            /* RECENCY, which Shopify cannot express at all.

               A smart collection rule has no created or published column, so
               Just Landed could only ever be approximated in Shopify by a sort
               order over a collection that still holds everything. Column 7 is
               the created stamp the builder already writes, so the narrowing
               happens here, exactly as maxPrice does for Under 25.

               The map stores a WINDOW, not a cutoff instant. A cutoff would be
               right until the page was cached and then quietly wrong forever
               after; a window is resolved against the shopper's clock on every
               load, so a map nobody rebuilds still means the same thing a week
               later.

               created of 0 means the builder had no date for that product,
               which is not the same as it being old. Including it would put an
               undated product at the top of a newest first shelf. */
            var floor = (Date.now() / 1000) - (scope.withinDays * 86400);
            all = all.filter(function (r) { return r[7] > 0 && r[7] >= floor; });
          }
        }

        index = { dict: merged };
        rows = all;
        api.ready = true;
        api.dict = merged;
        api.total = rows.length;

        byHandle = {};
        for (var i = 0; i < rows.length; i++) byHandle[rows[i][0]] = rows[i];
        return api;
      });
    },

    /* Selections arrive as arrays of dictionary LABELS, which is what a UI
       naturally has, and are converted to codes here so no caller has to know
       the index encoding. */
    encode: function (facet, labels) {
      var d = (index.dict[facet] || []);
      var out = [];
      (labels || []).forEach(function (label) {
        var at = d.indexOf(label);
        if (at > -1) out.push(at);
      });
      return out;
    },

    query: function (selection, sortKey) {
      var sel = {
        preorder: !!selection.preorder,
        onSale: !!selection.onSale,
        newIn: !!selection.newIn,
        availability: selection.availability || [],
        priceMin: selection.priceMin != null ? selection.priceMin : null,
        priceMax: selection.priceMax != null ? selection.priceMax : null
      };
      FACETS.forEach(function (f) { sel[f] = api.encode(f, selection[f]); });
      return { handles: apply(sel, sortKey), counts: counts(sel) };
    },

    /* Keyed to the values the theme's own sort_by control already uses, so
       that control can drive this engine instead of a second one being added
       beside it. most-relevant and best-selling are absent on purpose: both
       need data Shopify holds and the index does not, so offering them would
       mean a control that silently does nothing. */
    /* `native` is the Shopify sort each option corresponds to. It is also what
       maps a collection's own default_sort_by onto one of these keys, so the
       page opens in the order the merchant set. */
    sorts: [
      { key: 'featured',     native: 'manual',            label: 'Featured' },
      { key: 'best-selling', native: 'best-selling',      label: 'Best selling' },
      { key: 'title-asc',  native: 'title-ascending',     label: 'Alphabetically, A-Z' },
      { key: 'title-desc', native: 'title-descending',    label: 'Alphabetically, Z-A' },
      { key: 'price-asc',  native: 'price-ascending',     label: 'Price, low to high' },
      { key: 'price-desc', native: 'price-descending',    label: 'Price, high to low' },
      { key: 'date-asc',   native: 'created-ascending',   label: 'Date, old to new' },
      { key: 'date-desc',  native: 'created-descending',  label: 'Date, new to old' }
    ],

    show: function (grid, handles, page) {
      var start = (page || 0) * PAGE_SIZE;
      var slice = handles.slice(start, start + PAGE_SIZE);
      /* The template has to be captured while the server-rendered cards are
         still in the grid, so this runs before the first replace. */
      if (!captureTemplate(grid)) return Promise.resolve(0);
      return hydrate(slice).then(function (products) {
        render(grid, products, true);
        return products.length;
      });
    },

    append: function (grid, handles) {
      if (!captureTemplate(grid)) return Promise.resolve(0);
      return hydrate(handles).then(function (products) {
        render(grid, products, false);
        return products.length;
      });
    },

    /* A plain property would capture 24 before init could change it. */
    get pageSize() { return PAGE_SIZE; },
    facets: FACETS
  };

  window.LLFilter = api;
}());
